import React, { Component } from "react";
import { Empty, Table, Tag, Tooltip, Typography } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import XLSXStyle from "xlsx-js-style";
import moment from "moment";

import { ButtonItem, dineroDisabledMask } from "../../../../Components/Items";

const { Text } = Typography;

// Formato contable: sin esto (y sin t:"n") Excel escribe los importes como texto
const FORMATO_MONEDA = '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';

class ComparativoCotizaciones extends Component {
  // Solo entran al comparativo las cotizaciones recibidas y no canceladas
  get cotizacionesVigentes() {
    return (this.props.cotizaciones || []).filter(
      (valueCotizacion) => valueCotizacion.estado === 2
    );
  }

  /**
   * Un renglon por insumo de la requisicion con el precio de cada proveedor y cual es
   * el mejor. Se arma con un Map por cotizacion para no recorrer las partidas por celda.
   */
  get filas() {
    const cotizaciones = this.cotizacionesVigentes;

    const partidasPorCotizacion = cotizaciones.map((valueCotizacion) => {
      const mapa = new Map();
      (valueCotizacion.partidas || []).forEach((valuePartida) => {
        if (valuePartida.noCotiza) return;
        if (valuePartida.precio === undefined || valuePartida.precio === null) return;
        mapa.set(valuePartida.insumoId.toString(), valuePartida);
      });
      return mapa;
    });

    return (this.props.insumos || []).map((valueInsumo) => {
      const insumoId = valueInsumo.insumoId.toString();
      const precios = {};
      let mejorPrecio = null;

      cotizaciones.forEach((valueCotizacion, indice) => {
        const partida = partidasPorCotizacion[indice].get(insumoId);
        if (!partida) return;
        precios[valueCotizacion._id] = {
          precio: partida.precio,
          cantidad: partida.cantidad,
          importe: (partida.cantidad || 0) * (partida.precio || 0),
        };
        if (mejorPrecio === null || partida.precio < mejorPrecio) {
          mejorPrecio = partida.precio;
        }
      });

      return { ...valueInsumo, key: insumoId, precios, mejorPrecio };
    });
  }

  get totalesPorCotizacion() {
    const totales = {};
    this.cotizacionesVigentes.forEach((valueCotizacion) => {
      totales[valueCotizacion._id] = valueCotizacion.total || 0;
    });
    return totales;
  }

  exportarExcel = () => {
    const cotizaciones = this.cotizacionesVigentes;
    const filas = this.filas;

    const encabezados = ["Clave", "Descripción", "Unidad", "Cantidad"];
    cotizaciones.forEach((valueCotizacion) => {
      encabezados.push(valueCotizacion.proveedor?.nombre || `Cotización ${valueCotizacion.numero}`);
    });
    encabezados.push("Mejor Precio");

    const datos = [encabezados];

    filas.forEach((valueFila) => {
      const renglon = [
        valueFila.claveInt || "",
        valueFila.descripcion || "",
        valueFila.unidad || "",
        { v: valueFila.cantidad || 0, t: "n" },
      ];
      cotizaciones.forEach((valueCotizacion) => {
        const celda = valueFila.precios[valueCotizacion._id];
        renglon.push(
          celda
            ? { v: celda.precio, t: "n", s: { numFmt: FORMATO_MONEDA } }
            : ""
        );
      });
      renglon.push(
        valueFila.mejorPrecio !== null
          ? { v: valueFila.mejorPrecio, t: "n", s: { numFmt: FORMATO_MONEDA } }
          : ""
      );
      datos.push(renglon);
    });

    const totales = this.totalesPorCotizacion;
    const renglonTotales = ["", "TOTAL", "", ""];
    cotizaciones.forEach((valueCotizacion) => {
      renglonTotales.push({
        v: totales[valueCotizacion._id] || 0,
        t: "n",
        s: { numFmt: FORMATO_MONEDA, font: { bold: true } },
      });
    });
    renglonTotales.push("");
    datos.push(renglonTotales);

    const worksheet = XLSXStyle.utils.aoa_to_sheet(datos);
    worksheet["!cols"] = [
      { wch: 14 },
      { wch: 45 },
      { wch: 10 },
      { wch: 10 },
      ...cotizaciones.map(() => ({ wch: 18 })),
      { wch: 16 },
    ];

    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, worksheet, "Comparativo");
    XLSXStyle.writeFile(
      workbook,
      `Comparativo_Cotizaciones_${moment().format("YYYYMMDD")}.xlsx`
    );
  };

  render() {
    const cotizaciones = this.cotizacionesVigentes;

    if (cotizaciones.length === 0) {
      return (
        <Empty description="Aún no hay cotizaciones recibidas que comparar" />
      );
    }

    const filas = this.filas;
    const totales = this.totalesPorCotizacion;

    const columnas = [
      { title: "Clave", dataIndex: "claveInt", width: 110, fixed: "left" },
      { title: "Descripción", dataIndex: "descripcion", width: 260, fixed: "left" },
      { title: "Unidad", dataIndex: "unidad", width: 80 },
      { title: "Cantidad", dataIndex: "cantidad", width: 90, align: "right" },
      ...cotizaciones.map((valueCotizacion) => ({
        title: (
          <Tooltip
            title={
              <>
                <div>Solicitud/Cotización {valueCotizacion.numero}</div>
                {valueCotizacion.vigencia && (
                  <div>
                    Vigencia: {moment(valueCotizacion.vigencia).format("DD/MM/YYYY")}
                  </div>
                )}
                {valueCotizacion.tiempoEntrega && (
                  <div>Entrega: {valueCotizacion.tiempoEntrega}</div>
                )}
                {valueCotizacion.condicionesPago && (
                  <div>Pago: {valueCotizacion.condicionesPago}</div>
                )}
              </>
            }
          >
            <span>{valueCotizacion.proveedor?.nombre || `Cotización ${valueCotizacion.numero}`}</span>
          </Tooltip>
        ),
        key: valueCotizacion._id,
        width: 150,
        align: "right",
        render: (_, valueFila) => {
          const celda = valueFila.precios[valueCotizacion._id];
          if (!celda) return <Text type="secondary">—</Text>;
          const esMejor =
            valueFila.mejorPrecio !== null && celda.precio === valueFila.mejorPrecio;
          return (
            <div>
              <div style={{ fontWeight: esMejor ? "bold" : "normal" }}>
                {dineroDisabledMask(celda.precio)}
                {esMejor && (
                  <Tag color="green" style={{ marginLeft: 5 }}>
                    Mejor
                  </Tag>
                )}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {dineroDisabledMask(celda.importe)}
              </Text>
            </div>
          );
        },
      })),
    ];

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <ButtonItem icon={<FileExcelOutlined />} onClick={this.exportarExcel}>
            Exportar a Excel
          </ButtonItem>
        </div>
        <Table
          size="small"
          rowKey="key"
          columns={columnas}
          dataSource={filas}
          pagination={false}
          scroll={{ x: 900 }}
          summary={() => (
            // antd 4.15.6: Table.Summary.Cell todavia no acepta `align` ni Summary `fixed`,
            // por eso la alineacion va en el contenido de la celda.
            <Table.Summary>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>Total cotizado</Text>
                </Table.Summary.Cell>
                {cotizaciones.map((valueCotizacion, indice) => (
                  <Table.Summary.Cell key={valueCotizacion._id} index={4 + indice}>
                    <div style={{ textAlign: "right" }}>
                      <Text strong>
                        {dineroDisabledMask(totales[valueCotizacion._id] || 0)}
                      </Text>
                    </div>
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
    );
  }
}

export default ComparativoCotizaciones;
